import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAllSmsTemplates,
  getSmsPlaceholders,
  postCreateOrUpdateSmsTemplate,
  deleteSmsTemplate,
  type CreateOrUpdateSmsTemplateRequest,
  type GetAllSmsTemplatesResponse,
  type GetSmsPlaceholdersResponse,
  type SmsTemplate,
} from './smsTemplates';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

const smsTemplateKeys = {
  all: ['smsTemplates'] as const,
  placeholders: ['smsTemplates', 'placeholders'] as const,
};

export const useSmsTemplates = () => {
  return useQuery<GetAllSmsTemplatesResponse>({
    queryKey: smsTemplateKeys.all,
    queryFn: () => getAllSmsTemplates(),
  });
};

export const useSmsPlaceholders = () => {
  return useQuery<GetSmsPlaceholdersResponse>({
    queryKey: smsTemplateKeys.placeholders,
    queryFn: () => getSmsPlaceholders(),
    staleTime: Infinity,
  });
};

export const useCreateOrUpdateSmsTemplate = () => {
  const queryClient = useQueryClient();
  const addSnackbarMessage = useAddSnackbarMessage();
  const { trackEvent } = useAnalytics();

  return useMutation<SmsTemplate, Error, CreateOrUpdateSmsTemplateRequest>({
    mutationFn: (data: CreateOrUpdateSmsTemplateRequest) => postCreateOrUpdateSmsTemplate(data),
    onSuccess: (_, variables) => {
      trackEvent('sms_template_saved', {
        template_type: variables.type,
        is_update: Boolean(variables.content),
      });
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sms'] });
      addSnackbarMessage({ text: 'SMS šablona byla úspěšně uložena', type: 'success' });
    },
    onError: error => {
      const message = error.message;
      addSnackbarMessage({ text: message, type: 'error' });
    },
  });
};

export const useDeleteSmsTemplate = () => {
  const queryClient = useQueryClient();
  const addSnackbarMessage = useAddSnackbarMessage();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteSmsTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: ['sms'] });
      addSnackbarMessage({ text: 'SMS šablona byla smazána', type: 'success' });
    },
    onError: error => {
      const message = error.message;
      addSnackbarMessage({ text: message, type: 'error' });
    },
  });
};
